/* ===========================================================
   Find Your Matcha — Landing Page JS
   Asset path: assets/matcha-lp.js

   Idempotent: safe to load multiple times. Initialization is
   guarded so re-running doesn't double-bind handlers.
   =========================================================== */

(function () {
  if (window.__matchaLpInit) return;
  window.__matchaLpInit = true;

  function init() {
    initPillScrollSpy();
    initCarouselDots();
    initVariantSelects();
    initAddToCartFeedback();
    initImageFallback();
    initFaqAccordion();
    initRecipeModal();
    initKlaviyoReviews();
  }

  /* Pill nav scroll-spy: highlights the pill matching the section in view.
     Auto-discovers sections by following pill href targets. */
  function initPillScrollSpy() {
    var pills = document.querySelectorAll('.matcha-lp [data-pill]');
    if (!pills.length) return;

    var observed = [];
    pills.forEach(function (pill) {
      var href = pill.getAttribute('href') || '';
      var targetId = href.replace(/^#/, '');
      if (!targetId) return;
      var el = document.getElementById(targetId);
      if (el) observed.push({ el: el, id: targetId });
    });

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var id = entry.target.id;
          pills.forEach(function (p) {
            var t = (p.getAttribute('href') || '').replace(/^#/, '');
            p.classList.toggle('active', t === id);
          });
        }
      });
    }, { rootMargin: '-30% 0px -60% 0px', threshold: 0 });

    observed.forEach(function (item) { observer.observe(item.el); });
  }

  /* Carousel dots — one dot per scroll position.
     Position count = max(1, cards - visibleCount + 1). VisibleCount = 2. */
  function initCarouselDots() {
    document.querySelectorAll('.matcha-lp [data-dots]').forEach(function (dotsContainer) {
      var carousel = dotsContainer.previousElementSibling;
      if (!carousel || !carousel.hasAttribute('data-carousel')) return;

      var cards = carousel.querySelectorAll('.product-card, .recipe-card');
      var visibleCount = 2;
      var positionCount = Math.max(1, cards.length - visibleCount + 1);
      if (positionCount < 2) return;

      for (var i = 0; i < positionCount; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'carousel-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', 'Go to position ' + (i + 1));
        (function (cardIndex) {
          dot.addEventListener('click', function () {
            cards[cardIndex].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          });
        })(i);
        dotsContainer.appendChild(dot);
      }

      var scrollTimeout;
      carousel.addEventListener('scroll', function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          var cardStep = cards[0].getBoundingClientRect().width + 12;
          var activeIndex = Math.round(carousel.scrollLeft / cardStep);
          var clamped = Math.min(Math.max(activeIndex, 0), positionCount - 1);
          dotsContainer.querySelectorAll('.carousel-dot').forEach(function (dot, i) {
            dot.classList.toggle('active', i === clamped);
          });
        }, 60);
      }, { passive: true });
    });
  }

  /* Variant selects on multi-variant product cards.
     Updates the displayed price and CTA state when a variant changes. */
  function initVariantSelects() {
    document.querySelectorAll('.matcha-lp [data-variant-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        if (!option) return;
        var card = select.closest('.product-card');
        if (!card) return;
        var priceEl = card.querySelector('[data-product-price]');
        if (priceEl && option.dataset.price) {
          priceEl.textContent = option.dataset.price;
        }
        var btn = card.querySelector('.product-cta');
        if (btn) {
          var available = option.dataset.available !== 'false';
          btn.disabled = !available;
          btn.textContent = available ? 'Buy It Now' : 'Sold Out';
        }
      });
    });
  }

  /* Add-to-cart: posts to /cart/add.js, then updates cart counts,
     dispatches multiple events for theme compatibility, and shows
     a theme-agnostic toast notification with a "View cart" link. */
  function initAddToCartFeedback() {
    document.querySelectorAll('.matcha-lp .product-form').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('.product-cta');
        if (!btn || btn.disabled) return;

        var formData = new FormData(form);
        var original = btn.textContent;
        btn.textContent = 'Adding…';
        btn.disabled = true;

        fetch('/cart/add.js', { method: 'POST', body: formData })
          .then(function (r) {
            if (!r.ok) {
              return r.json().then(function (err) {
                throw new Error(err.description || err.message || 'Buy It Now failed');
              });
            }
            return r.json();
          })
          .then(function () {
            // Pull fresh cart state, update counts + dispatch events
            return fetch('/cart.js').then(function (r) { return r.json(); });
          })
          .then(function (cart) {
            updateCartCounts(cart.item_count);
            ['cart:refresh', 'cart:update', 'cart:added', 'cart-drawer:open', 'cart:open'].forEach(function (name) {
              document.dispatchEvent(new CustomEvent(name, { detail: { cart: cart } }));
            });
            // Open Harney's theme cart drawer (custom <cart-drawer> element).
            // It re-renders its own contents via _rerenderCart() and opens
            // when the `open` attribute is set. Fall back to a toast if the
            // drawer isn't present (e.g. theme change).
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer._rerenderCart === 'function') {
              try {
                drawer._rerenderCart();
                drawer.setAttribute('open', '');
              } catch (e) {
                showCartToast('Added to cart');
              }
            } else {
              showCartToast('Added to cart');
            }
            btn.textContent = 'Added ✓';
            btn.style.background = '#6B8A48';
            setTimeout(function () {
              btn.textContent = original;
              btn.style.background = '';
              btn.disabled = false;
            }, 1500);
          })
          .catch(function () {
            btn.textContent = 'Try again';
            setTimeout(function () {
              btn.textContent = original;
              btn.disabled = false;
            }, 1800);
          });
      });
    });
  }

  /* Update every visible cart-count badge we can find (themes use
     wildly different selectors — try the common ones). */
  function updateCartCounts(count) {
    var selectors = [
      '[data-cart-count]',
      '.cart-count',
      '.cart-count-bubble',
      '.cart-link__bubble-num',
      '.header__cart-count',
      '.site-cart-count',
      '#CartCount',
      '.cart .label',
      'a[href="/cart"] .label',
      'a[href="/cart"] [data-count]'
    ];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.textContent = String(count);
        // Some themes hide the badge at 0 — show it when non-zero
        if (count > 0) el.style.display = '';
      });
    });
  }

  /* Theme-agnostic toast notification at top of viewport.
     Singleton — re-used across multiple adds. */
  function showCartToast(message) {
    var toast = document.getElementById('matcha-lp-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'matcha-lp-toast';
      toast.className = 'matcha-lp-toast';
      toast.innerHTML =
        '<span class="matcha-lp-toast-msg"></span>' +
        '<a href="/cart" class="matcha-lp-toast-link">View cart →</a>';
      document.body.appendChild(toast);
    }
    toast.querySelector('.matcha-lp-toast-msg').textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._matchaLpToastTimeout);
    toast._matchaLpToastTimeout = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 3500);
  }

  /* If a product image fails to load, swap in a styled gradient placeholder
     with the product name so the card stays readable. */
  function initImageFallback() {
    function handleError(img) {
      var parent = img.parentElement;
      if (!parent || parent.querySelector('.product-image-fallback')) return;
      img.style.display = 'none';
      var label = parent.getAttribute('data-fallback') || img.alt || 'Product';
      var fb = document.createElement('div');
      fb.className = 'product-image-fallback';
      fb.textContent = label;
      parent.appendChild(fb);
    }
    document.querySelectorAll('.matcha-lp .product-image img, .matcha-lp .recipe-image img').forEach(function (img) {
      if (img.complete && img.naturalWidth === 0) {
        handleError(img);
      } else {
        img.addEventListener('error', function () { handleError(img); });
      }
    });
  }

  /* FAQ accordion */
  function initFaqAccordion() {
    document.querySelectorAll('.matcha-lp .faq-q').forEach(function (q) {
      q.addEventListener('click', function () {
        var item = q.closest('.faq-item');
        if (item) item.classList.toggle('is-open');
      });
    });
  }

  /* Recipe modal — opens on card click, closes via overlay/X/Escape,
     prints via window.print. Hash deep-linking: #recipe-iced-matcha
     auto-opens that recipe. Email-friendly. */
  function initRecipeModal() {
    var modal = document.getElementById('recipeModal');
    var modalContent = document.getElementById('recipeModalContent');
    if (!modal || !modalContent) return;

    function open(recipeId) {
      var source = document.getElementById('recipe-' + recipeId);
      if (!source) return;
      modalContent.innerHTML = source.innerHTML;
      modalContent.scrollTop = 0;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('matcha-modal-open');
      var newHash = '#recipe-' + recipeId;
      if (window.location.hash !== newHash) {
        history.replaceState(null, '', newHash);
      }
    }

    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('matcha-modal-open');
      if (window.location.hash.indexOf('#recipe-') === 0) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }

    function openFromHash() {
      var match = window.location.hash.match(/^#recipe-(.+)$/);
      if (match && document.getElementById('recipe-' + match[1])) {
        setTimeout(function () { open(match[1]); }, 150);
      }
    }

    window.addEventListener('load', openFromHash);
    window.addEventListener('hashchange', openFromHash);

    document.querySelectorAll('.matcha-lp .recipe-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        e.preventDefault();
        var recipeId = card.getAttribute('data-recipe');
        if (recipeId) open(recipeId);
      });
    });

    document.querySelectorAll('.matcha-lp [data-recipe-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    var printBtn = modal.querySelector('.recipe-modal-print');
    if (printBtn) {
      printBtn.addEventListener('click', function () { window.print(); });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }

  /* Klaviyo Reviews — fetches 5-star reviews for the products selected
     in the reviews section blocks and swaps placeholder quotes for the
     real thing. Silent failure: if the endpoint is blank, unreachable,
     or returns no reviews for a given product, the manual fallback
     copy entered in the block remains. */
  function initKlaviyoReviews() {
    var container = document.querySelector('.matcha-lp .reviews[data-reviews-endpoint]');
    if (!container) return;
    var endpoint = container.getAttribute('data-reviews-endpoint');
    var productIds = container.getAttribute('data-product-ids');
    if (!endpoint || !productIds) return;

    var sep = endpoint.indexOf('?') === -1 ? '?' : '&';
    var url = endpoint + sep + 'product_ids=' + encodeURIComponent(productIds);

    fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.reviews || !data.reviews.length) return;
        // First review per product wins
        var byProduct = {};
        data.reviews.forEach(function (rev) {
          var pid = String(rev.product_id);
          if (!byProduct[pid]) byProduct[pid] = rev;
        });
        container.querySelectorAll('.review-card[data-product-id]').forEach(function (card) {
          // Skip cards admin has pinned with manual content
          if (card.getAttribute('data-pinned') === 'true') return;
          var pid = card.getAttribute('data-product-id');
          var rev = byProduct[pid];
          if (!rev) return;
          var quoteEl = card.querySelector('.review-quote');
          var authorEl = card.querySelector('.review-author');
          if (quoteEl && rev.content) {
            quoteEl.textContent = rev.content;
          }
          if (authorEl && rev.author_name) {
            authorEl.textContent = '— ' + rev.author_name;
          }
        });
      })
      .catch(function () { /* silent — fallback content stays */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();